import { createFileRoute, notFound } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { NOT_FOUND_HEAD_FALLBACK } from "@/lib/brandIcons";
import { blogPostHead } from "@/lib/blog/seo";
import { BlogPostArticle } from "#/components/blog/BlogPostArticle";
import { DraftPreviewBanner } from "@/components/publish/DraftPreviewBanner";

// App-origin fallback for the signed draft preview (#475). Used only when the
// workspace has NO custom_domain (otherwise the editor opens the preview on that
// domain, served by the blog renderer — see functions/src/blog/renderBlogCore.ts).
//
// This route is the hardened replacement for the old client-only
// `/preview/$workspaceId/$postId` SPA route:
//   • SSR (not ssr:false), so the token gate runs server-side and no public HTML
//     shell exists for an anonymous caller — the draft is loaded only after the
//     signed token verifies;
//   • authorised by the `?token=` query (workspaceId lives inside the token, not
//     the URL), minted by `createPreviewToken` after `checkPublishPermission`;
//   • de-indexed by a REAL `X-Robots-Tag` response header (not a client meta tag),
//     so a non-JS crawler that fetches the URL still sees noindex;
//   • rendered through the SAME `BlogPostArticle` as the published `/blog/$slug`
//     page — what the reader will actually see.
// Any token failure (missing / invalid / expired / wrong post) → 404, never 401/403.

const NOINDEX = "noindex, nofollow";

// Verify the token server-side, then load the draft. Returns undefined for any
// failure so the loader turns it into a 404 without confirming the post exists.
const loadPreview = createServerFn({ method: "GET" })
  .validator((data: { postId: string; token: string }) => data)
  .handler(async ({ data }) => {
    const { verifyPreviewToken } = await import(
      "../../functions/src/blog/previewToken"
    );
    const claims = verifyPreviewToken(data.token);
    // The token binds a workspace + post; the URL supplies the post. Both must agree.
    if (!claims || claims.postId !== data.postId) return undefined;
    const { loadDraftPostById } = await import("@/lib/blog/queries");
    return loadDraftPostById(claims.workspaceId, data.postId);
  });

export const Route = createFileRoute("/preview/$postId")({
  ssr: true,
  validateSearch: (search: Record<string, unknown>) => ({
    token: typeof search.token === "string" ? search.token : "",
  }),
  loaderDeps: ({ search }) => ({ token: search.token }),
  loader: async ({ params, deps }) => {
    const data = await loadPreview({
      data: { postId: params.postId, token: deps.token },
    });
    if (!data) throw notFound();
    return data;
  },
  // A REAL server header on every response — the primary de-index guarantee, and
  // never cache a draft (it changes on each save). Set even on the 404 path.
  headers: () => ({
    "X-Robots-Tag": NOINDEX,
    "cache-control": "no-store",
  }),
  // Same <head> as the published post (title/OG/JSON-LD). De-indexing is enforced by
  // the real `X-Robots-Tag: noindex, nofollow` response header set above — the
  // authoritative guarantee (a client `<meta robots>` alone wouldn't stop a non-JS
  // crawler that fetches the URL), applied on the 200 AND the 404 path.
  // notFound()-branch icon fallback + the `as never` cast it needs: see
  // `NOT_FOUND_HEAD_FALLBACK`'s docblock (#1099 / US2).
  head: ({ loaderData }) =>
    (loaderData
      ? blogPostHead(
          loaderData.blog,
          loaderData.post,
          loaderData.canonical,
          loaderData.feedUrl,
          loaderData.itemList,
          loaderData.faqPage,
        )
      : NOT_FOUND_HEAD_FALLBACK) as never,
  component: PreviewPage,
});

function PreviewPage() {
  const { post, htmlBody, morePosts } = Route.useLoaderData();
  return (
    <div>
      <DraftPreviewBanner status={post.status} />
      {/* The block the published page shows, so the preview is a preview of the
          whole page — the same reason the themed `previewPost` renders it. */}
      <BlogPostArticle post={post} htmlBody={htmlBody} morePosts={morePosts} />
    </div>
  );
}
